import { useState } from "react";
import { API } from "@/lib/api";

/**
 * Minimal in-page network diagnostic. Runs three probes against the API
 * (same-origin assumed) and surfaces the actual failure mode in plain
 * English so we can tell whether the browser is hitting DNS / TLS /
 * a stale Service Worker / a real backend 5xx.
 *
 * Stays inside the catalog error fallback — never shown on the happy path.
 */
export default function DiagnosticPanel() {
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);

  const run = async () => {
    setRunning(true);
    setResults(null);
    const out = {
      backendUrl: API,
      origin: window.location.origin,
      online: navigator.onLine,
      serviceWorker: "checking…",
      caches: "checking…",
      priceBounds: "checking…",
      seriesList: "checking…",
      filesEndpoint: "checking…",
    };

    // Service worker status
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      out.serviceWorker = regs.length
        ? regs.map((r) => `${r.scope} (${r.active ? "active" : "inactive"})`).join("; ")
        : "none";
    } else {
      out.serviceWorker = "unsupported";
    }

    // Cache storage status
    if ("caches" in window) {
      const keys = await caches.keys();
      out.caches = keys.length ? keys.join(", ") : "empty";
    } else {
      out.caches = "unsupported";
    }

    // Probe each endpoint with a fresh fetch (cache-busting query string) so we
    // bypass any HTTP cache and see the real network state. AbortController gives
    // us a fast 6-second timeout — saves a long hang on broken networks.
    const probe = async (path) => {
      try {
        const c = new AbortController();
        const t = setTimeout(() => c.abort(), 6000);
        const r = await fetch(`${API}${path}${path.includes("?") ? "&" : "?"}_=${Date.now()}`, {
          method: "GET",
          cache: "no-store",
          signal: c.signal,
        });
        clearTimeout(t);
        const text = await r.text();
        return `HTTP ${r.status} · ${text.length} bytes${r.status >= 400 ? ` · ${text.slice(0, 80)}` : ""}`;
      } catch (e) {
        return `FAILED · ${e?.name || "Error"}: ${e?.message || String(e)}`;
      }
    };

    out.priceBounds = await probe("/series/price-bounds");
    out.seriesList = await probe("/series");
    // Hit an arbitrary asset path; 404 means routing works (real asset id unknown), network err means proxy/SW issue
    out.filesEndpoint = await probe("/files/nonexistent.jpg");

    setResults(out);
    setRunning(false);
  };

  return (
    <div className="mt-4 p-4 bg-[#FFF8E8] border border-[#FCD34D] text-left text-xs space-y-2">
      <button
        type="button"
        onClick={run}
        disabled={running}
        data-testid="catalog-diag-run"
        className="bg-[#D62828] hover:bg-[#8B1717] disabled:opacity-60 text-white px-3 py-1.5 tracking-luxe text-[10px]"
      >
        {running ? "Running…" : "Run diagnostic"}
      </button>
      {results && (
        <div className="font-mono text-[11px] leading-relaxed text-[#1A1A1A] break-words">
          <div><span className="text-[#D62828]">backendUrl:</span> {results.backendUrl}</div>
          <div><span className="text-[#D62828]">origin:</span> {results.origin}</div>
          <div><span className="text-[#D62828]">online:</span> {String(results.online)}</div>
          <div><span className="text-[#D62828]">serviceWorker:</span> {results.serviceWorker}</div>
          <div><span className="text-[#D62828]">caches:</span> {results.caches}</div>
          <div><span className="text-[#D62828]">/series/price-bounds:</span> {results.priceBounds}</div>
          <div><span className="text-[#D62828]">/series:</span> {results.seriesList}</div>
          <div><span className="text-[#D62828]">/files/* (routing):</span> {results.filesEndpoint}</div>
          <div className="mt-3 p-2 bg-white border border-[#E5E0D8]">
            <strong>Copy this whole report and send it to your developer.</strong> If you see &quot;FAILED · TypeError&quot; or &quot;NetworkError&quot;, your phone&apos;s network is blocking the API. If you see &quot;HTTP 200&quot; here but the catalog still says &quot;Failed&quot;, the issue is JavaScript-side and a fresh install will fix it.
          </div>
        </div>
      )}
    </div>
  );
}
